// Generated by AI on 06/30/2026.
import { get } from 'lodash';

import React, {
    useState, useCallback, useMemo, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';

const CandidatesStoreContext = React.createContext({
    // State values (BehaviorSubject equivalents)
    isCandidatesListLoading: false,
    visibleCandidates: [],
    visibleCandidate: {},
    allCandidatesTalentIds: [],
    refreshReviewCandidatesListSmoothTrigger: 0,
    nextAIUpdateRatedCandidates: undefined,
    nextAIUpdateStartRatedCandidates: undefined,
    AIUpdateProgress: 0,
    isNeedToRunGeneratorBeforeListRefresh: false,

    // Setters
    setIsCandidatesListLoading: () => {},
    setVisibleCandidates: () => {},
    setVisibleCandidate: () => {},
    setAllCandidatesTalentIds: () => {},
    setNextAIUpdateRatedCandidates: () => {},
    setNextAIUpdateStartRatedCandidates: () => {},
    setAIUpdateProgress: () => {},
    setIsNeedToRunGeneratorBeforeListRefresh: () => {},

    // Refs
    visibleCandidatesRef: { current: [] },
    visibleCandidateRef: { current: {} },

    // Event triggers (Subject equivalents - counter pattern)
    refreshCandidatesListTrigger: 0,
    refreshReviewCandidatesListTrigger: 0,
    refreshReviewCandidatesListQueue: [],
    refreshProfileTrigger: 0,
    refreshProfileQueue: [],

    // Event emitters
    refreshCandidatesList: () => {},
    refreshReviewCandidatesList: () => {},
    refreshReviewCandidatesListSmooth: () => {},
    refreshReviewCandidatesListSmoothAfterScroll: () => {},
    refreshProfile: () => {},
});

export const AppWithCandidatesStore = ({ children }) => {
    // State values
    const [isCandidatesListLoading, setIsCandidatesListLoading] = useState(false);
    const [visibleCandidates, setVisibleCandidates] = useState([]);
    const [visibleCandidate, setVisibleCandidate] = useState({});
    const [allCandidatesTalentIds, setAllCandidatesTalentIds] = useState([]);
    const [refreshReviewCandidatesListSmoothTrigger, setRefreshReviewCandidatesListSmoothTrigger] = useState(0);
    const [nextAIUpdateRatedCandidates, setNextAIUpdateRatedCandidates] = useState(undefined);
    const [nextAIUpdateStartRatedCandidates, setNextAIUpdateStartRatedCandidates] = useState(undefined);
    const [AIUpdateProgress, setAIUpdateProgress] = useState(0);
    const [isNeedToRunGeneratorBeforeListRefresh, setIsNeedToRunGeneratorBeforeListRefresh] = useState(false);

    // Event triggers (counter-based)
    const [refreshCandidatesListTrigger, setRefreshCandidatesListTrigger] = useState(0);
    const [refreshReviewCandidatesListTrigger, setRefreshReviewCandidatesListTrigger] = useState(0);
    // Modified by AI on 07/21/2026. Edit #1.
    // Queue for refreshReviewCandidatesList payloads to prevent event coalescing
    const [refreshReviewCandidatesListQueue, setRefreshReviewCandidatesListQueue] = useState([]);
    const [refreshProfileTrigger, setRefreshProfileTrigger] = useState(0);
    // Modified by AI on 07/21/2026. Edit #1.
    // Queue for refreshProfile payloads to prevent event coalescing
    const [refreshProfileQueue, setRefreshProfileQueue] = useState([]);

    // Modified by AI on 07/22/2026. Edit #6.
    // Centralized refs for accessing latest state in callbacks without adding dependencies
    const visibleCandidatesRef = useRef(visibleCandidates);
    const visibleCandidateRef = useRef(visibleCandidate);
    useEffect(() => { visibleCandidatesRef.current = visibleCandidates; }, [visibleCandidates]);
    useEffect(() => { visibleCandidateRef.current = visibleCandidate; }, [visibleCandidate]);

    // Modified by AI on 07/20/2026. Edit #9.
    // Ref for scroll-based smooth refresh (needs to read current state without re-render dependency)
    const allCandidatesTalentIdsRef = useRef(allCandidatesTalentIds);
    const isSmoothRefreshAfterScrollPendingRef = useRef(false);
    // Modified by AI on 07/20/2026. Edit #11.
    const smoothAfterScrollHandlerRef = useRef(null);

    useEffect(() => {
        allCandidatesTalentIdsRef.current = allCandidatesTalentIds;
    }, [allCandidatesTalentIds]);

    // Modified by AI on 07/20/2026. Edit #11.
    // Cleanup scroll listener on unmount to prevent memory leaks
    // eslint-disable-next-line arrow-body-style
    useEffect(() => {
        return () => {
            if (smoothAfterScrollHandlerRef.current) {
                document.removeEventListener('scroll', smoothAfterScrollHandlerRef.current);
                smoothAfterScrollHandlerRef.current = null;
            }
        };
    }, []);

    // Event emitters
    const refreshCandidatesList = useCallback(() => {
        setRefreshCandidatesListTrigger((prev) => prev + 1);
    }, []);

    // Modified by AI on 07/21/2026. Edit #1.
    const refreshReviewCandidatesList = useCallback(({
        isNeedToRunGenerator,
        showRecommendationsPopup,
        candidatesFilter,
        isNextUpdateRefresh,
        onFinishCallback,
    } = {}) => {
        // Queue the payload to prevent coalescing when multiple emits happen in same batch
        setRefreshReviewCandidatesListQueue((prev) => [...prev, {
            isNeedToRunGenerator,
            showRecommendationsPopup,
            candidatesFilter,
            isNextUpdateRefresh,
            onFinishCallback,
        }]);
        setRefreshReviewCandidatesListTrigger((prev) => prev + 1);
    }, []);

    const refreshReviewCandidatesListSmooth = useCallback(() => {
        setRefreshReviewCandidatesListSmoothTrigger((prev) => prev + 1);
    }, []);

    // Modified by AI on 07/20/2026. Edit #11.
    const refreshReviewCandidatesListSmoothAfterScroll = useCallback(({
        waitForScrollWhenCandidatesCountMoreThan = 3,
    } = {}) => {
        const candidatesCount = get(allCandidatesTalentIdsRef.current, 'length') || 0;
        if (candidatesCount > waitForScrollWhenCandidatesCountMoreThan) {
            // Wait for next scroll event, then trigger smooth refresh
            // Guard against multiple scroll listeners from rapid calls
            if (isSmoothRefreshAfterScrollPendingRef.current) return;
            isSmoothRefreshAfterScrollPendingRef.current = true;
            // Use named handler stored in ref for reliable cleanup
            const onScroll = () => {
                isSmoothRefreshAfterScrollPendingRef.current = false;
                smoothAfterScrollHandlerRef.current = null;
                setRefreshReviewCandidatesListSmoothTrigger((prev) => prev + 1);
            };
            smoothAfterScrollHandlerRef.current = onScroll;
            document.addEventListener('scroll', onScroll, { once: true });
        } else {
            setRefreshReviewCandidatesListSmoothTrigger((prev) => prev + 1);
        }
    }, []);

    // Modified by AI on 07/21/2026. Edit #1.
    const refreshProfile = useCallback(({ profileFeedbackModeUpdate } = {}) => {
        // Queue the payload to prevent coalescing when multiple emits happen in same batch
        setRefreshProfileQueue((prev) => [...prev, { profileFeedbackModeUpdate }]);
        setRefreshProfileTrigger((prev) => prev + 1);
    }, []);

    // Modified by AI on 07/21/2026. Edit #5.
    // Provider-managed queue compaction: truncate old entries to reclaim memory.
    // Consumers use lastSeenRef which is always >= the compaction boundary,
    // and access queue[i] || {} which safely returns {} for truncated indices.
    const QUEUE_COMPACTION_THRESHOLD = 50;
    useEffect(() => {
        if (refreshReviewCandidatesListQueue.length > QUEUE_COMPACTION_THRESHOLD) {
            // Keep only the last QUEUE_COMPACTION_THRESHOLD/2 entries
            const keepFrom = refreshReviewCandidatesListQueue.length - (QUEUE_COMPACTION_THRESHOLD / 2);
            setRefreshReviewCandidatesListQueue((prev) => {
                const next = new Array(prev.length);
                for (let i = keepFrom; i < prev.length; i++) {
                    next[i] = prev[i];
                }
                return next;
            });
        }
    }, [refreshReviewCandidatesListQueue.length]);

    useEffect(() => {
        if (refreshProfileQueue.length > QUEUE_COMPACTION_THRESHOLD) {
            const keepFrom = refreshProfileQueue.length - (QUEUE_COMPACTION_THRESHOLD / 2);
            setRefreshProfileQueue((prev) => {
                const next = new Array(prev.length);
                for (let i = keepFrom; i < prev.length; i++) {
                    next[i] = prev[i];
                }
                return next;
            });
        }
    }, [refreshProfileQueue.length]);

    const contextValue = useMemo(() => ({
        isCandidatesListLoading,
        visibleCandidates,
        visibleCandidate,
        allCandidatesTalentIds,
        refreshReviewCandidatesListSmoothTrigger,
        nextAIUpdateRatedCandidates,
        nextAIUpdateStartRatedCandidates,
        AIUpdateProgress,
        isNeedToRunGeneratorBeforeListRefresh,

        setIsCandidatesListLoading,
        setVisibleCandidates,
        setVisibleCandidate,
        setAllCandidatesTalentIds,
        setNextAIUpdateRatedCandidates,
        setNextAIUpdateStartRatedCandidates,
        setAIUpdateProgress,
        setIsNeedToRunGeneratorBeforeListRefresh,

        visibleCandidatesRef,
        visibleCandidateRef,

        refreshCandidatesListTrigger,
        refreshReviewCandidatesListTrigger,
        refreshReviewCandidatesListQueue,
        refreshProfileTrigger,
        refreshProfileQueue,

        refreshCandidatesList,
        refreshReviewCandidatesList,
        refreshReviewCandidatesListSmooth,
        refreshReviewCandidatesListSmoothAfterScroll,
        refreshProfile,
    }), [
        isCandidatesListLoading,
        visibleCandidates,
        visibleCandidate,
        allCandidatesTalentIds,
        refreshReviewCandidatesListSmoothTrigger,
        nextAIUpdateRatedCandidates,
        nextAIUpdateStartRatedCandidates,
        AIUpdateProgress,
        isNeedToRunGeneratorBeforeListRefresh,
        refreshCandidatesListTrigger,
        refreshReviewCandidatesListTrigger,
        refreshReviewCandidatesListQueue,
        refreshProfileTrigger,
        refreshProfileQueue,
        refreshCandidatesList,
        refreshReviewCandidatesList,
        refreshReviewCandidatesListSmooth,
        refreshReviewCandidatesListSmoothAfterScroll,
        refreshProfile,
    ]);

    return (
        <CandidatesStoreContext.Provider value={contextValue}>
            {children}
        </CandidatesStoreContext.Provider>
    );
};

AppWithCandidatesStore.propTypes = {
    children: PropTypes.node.isRequired,
};

export default CandidatesStoreContext;
